import type { PaymentPromiseInput } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { recordPaymentPromise, supersedePaymentPromise } from '../payment-promises.api';

/** Records a promise, or supersedes an open one when `supersedes` is set. A failure stays inline in the form; success toasts. */
export function useSavePromise(customerId: string, supersedes: string | null) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  /** Returns whether it saved, so the form stays open with its error showing when it did not. */
  async function save(input: PaymentPromiseInput): Promise<boolean> {
    setError(null);
    setSaving(true);
    try {
      await (supersedes ? supersedePaymentPromise(customerId, supersedes, input) : recordPaymentPromise(customerId, input));
      toast.success(supersedes ? 'Promise to pay replaced' : 'Promise to pay recorded');
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not save — check the fields and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  return { error, saving, save, resetError: () => setError(null) };
}
